import { Flex } from 'antd';
import { PrimaryCard } from '../../assets/styles/components/dashboard.styled';
import { Button, Divider } from '../Ant';
import CardData from '../dashboard/cardData';
import {
  ArrowRight,
  Briefcase,
  Calendar,
  Clipboard,
  ProfileUser,
  Receive,
  ShieldTick,
  UserOctagon,
} from '../icons';
import { useParams } from 'react-router-dom';
import { useFetchConflictChecksById } from '../../hooks/useFetchConflictChecksById';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { usePage } from '../../context/PageContext';
import { useEffect, useState } from 'react';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useNavigationStack } from '../../context/NavigationStackContext';
import DescriptionModal from '../descriptionModal/descriptionModal';
import { ROUTES } from '../../constants/constants';
import {
  conflictCheckCardBorderColorMapping,
  ConflictCheckFilterType,
  conflictCheckTagColorMapping,
} from '../../constants/conflictSearch';
import { AnalyticsPage } from '../../interface/analytics';
import { useAnalytics } from '../../hooks/useAnalytics';
import { getMatterIconClass } from '../../constants/utilityFunctions';
import SkeletonEntityCard from '../common/skeleton/skeletonEntityCard';
import DetailScrollArea from '../detailScrollArea/detailScrollArea';
import { getViewLabel } from '../../helpers/viewLabelHelper';

const ConflictCheckDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { setTitle } = usePage();
  const { trackPage } = useAnalytics();
  const { goBack, push } = useNavigationStack();
  const [isShowMoreModalOpen, setIsShowMoreModalOpen] = useState<boolean>(false);
  const [showMoreContent, setShowMoreContent] = useState<
    { title: string; content: string } | undefined
  >(undefined);
  const { data: conflictCheckDetails, isLoading: isConflictCheckDetailsLoading } =
    useFetchConflictChecksById({
      id,
      relations: [
        'responsibleUser:fullName',
        'checkedByUser:fullName',
        'principalPrivatePerson:fullName',
        'principalCompany:name',
        'matter:subject,matterNumber',
        'matter.principalCompany:name',
        'matter.principalPrivatePerson:fullName',
        'matterRequest:subject',
        'matterRequest.principalCompany:name',
        'matterRequest.principalPrivatePerson:fullName',
      ],
    });

  useEffect(() => {
    trackPage(AnalyticsPage.CONFLICT_CHECK_DETAIL);
  }, [trackPage]);

  const handleBack = () => {
    goBack();
  };
  const handleClose = () => {
    setIsShowMoreModalOpen(false);
  };

  const handleGoToCustomer = () => {
    push(
      ROUTES.CUSTOMER_LISTING +
        `/${conflictCheckDetails?.principalCompany?.id ?? conflictCheckDetails?.principalPrivatePerson?.id}`,
    );
  };

  const handleGoToMatter = () => {
    push(ROUTES.MATTER_DETAIL + `/${conflictCheckDetails?.matter?.id}`);
  };
  const handleGoToMatterRequest = () => {
    push(ROUTES.MATTER_REQUEST_DETAIL + `/${conflictCheckDetails?.matterRequest?.id}`);
  };

  useEffect(() => {
    if (!isConflictCheckDetailsLoading && conflictCheckDetails) {
      setTitle(
        <AddEditTransactionHeader
          title={conflictCheckDetails?.subject}
          subTitle={t('conflict:conflictCheck')}
          handleBack={handleBack}
        />,
      );
    }
  }, [isConflictCheckDetailsLoading, conflictCheckDetails]);

  if (isConflictCheckDetailsLoading) {
    return (
      <DetailScrollArea>
        <SkeletonEntityCard
          rows={[1, 1, 2, 1, 1]}
          withSubcontentOnFirstRow
          className="py-10 px-10 h-full"
        />
      </DetailScrollArea>
    );
  }
  return (
    <DetailScrollArea>
      <PrimaryCard
        $isDetail
        className={`d-flex flex-col gap-10 h-full conflict-card ${conflictCheckCardBorderColorMapping[conflictCheckDetails?.statusCode as ConflictCheckFilterType]}`}
      >
        <Flex justify="space-between" align="center" className="w-full">
          <Flex
            justify="center"
            align="center"
            className={`status-chip ${conflictCheckTagColorMapping[conflictCheckDetails?.statusCode as ConflictCheckFilterType]}`}
          >
            {t(`conflict:status.${conflictCheckDetails?.statusCode}`)}
          </Flex>
        </Flex>
        <Divider />
        <Flex vertical gap={10} className="flex-grow">
          <CardData
            icon={<Clipboard />}
            title={t('common:subject')}
            content={conflictCheckDetails?.subject}
            contentclass="lineclamp-1"
            moreAction={
              <Button
                data-testid="show-more-button"
                type="text"
                className="text-cta show-more"
                onClick={() => {
                  setShowMoreContent({
                    title: t('common:subject'),
                    content: conflictCheckDetails?.subject ?? '',
                  });
                  setIsShowMoreModalOpen(true);
                }}
              >
                {t('common:more')}
              </Button>
            }
          />
          <CardData
            icon={<ProfileUser />}
            title={t('customer:customer')}
            content={
              conflictCheckDetails?.principalCompany?.name ??
              conflictCheckDetails?.principalPrivatePerson?.fullName
            }
            contentclass="lineclamp-1"
            headingcontent={
              <Button
                data-testid="view-customer-button"
                type="text"
                icon={<ArrowRight />}
                iconPosition="end"
                className="text-cta"
                onClick={handleGoToCustomer}
              >
                {getViewLabel(t, 'customer:customer')}
              </Button>
            }
          />
          <CardData
            type="responsible-person"
            icon={<ShieldTick />}
            title={t('conflict:responsiblePerson')}
            content={conflictCheckDetails?.responsibleUser?.fullName}
          />
          <Flex justify="space-between">
            <CardData
              type="processed-by"
              icon={<UserOctagon />}
              title={t('conflict:processedBy')}
              content={conflictCheckDetails?.checkedByUser?.fullName}
            />
            <CardData
              type="created-on"
              icon={<Calendar />}
              title={t('conflict:createdOn')}
              content={dayjs(conflictCheckDetails?.createdOn).format('MMM DD, YYYY')}
            />
          </Flex>
          <Divider />
          <CardData
            type="matter"
            icon={
              <Briefcase
                className={getMatterIconClass(conflictCheckDetails?.matter?.invoiceableType ?? '')}
              />
            }
            title={t('matter:matter')}
            contentclass="lineclamp-1"
            subcontentclass="lineclamp-1"
            content={
              conflictCheckDetails?.matter?.principalCompany?.name ??
              conflictCheckDetails?.matter?.principalPrivatePerson?.fullName ??
              '-'
            }
            subcontent={
              conflictCheckDetails?.matter &&
              `${conflictCheckDetails.matter?.matterNumber} - ${conflictCheckDetails.matter?.subject}`
            }
            headingcontent={
              (conflictCheckDetails?.matter?.principalCompany ||
                conflictCheckDetails?.matter?.principalPrivatePerson) && (
                <Button
                  data-testid="view-matter-button"
                  type="text"
                  icon={<ArrowRight />}
                  iconPosition="end"
                  className="text-cta"
                  onClick={handleGoToMatter}
                >
                  {getViewLabel(t, 'matter:matter')}
                </Button>
              )
            }
          />
          <CardData
            icon={<Receive />}
            title={t('matterRequest:matterRequest')}
            contentclass="lineclamp-1"
            subcontentclass="lineclamp-1"
            type="matter-request"
            content={
              conflictCheckDetails?.matterRequest?.principalCompany?.name ??
              conflictCheckDetails?.matterRequest?.principalPrivatePerson?.fullName ??
              '-'
            }
            subcontent={
              conflictCheckDetails?.matterRequest &&
              `${conflictCheckDetails.matterRequest?.subject}`
            }
            headingcontent={
              (conflictCheckDetails?.matterRequest?.principalCompany ||
                conflictCheckDetails?.matterRequest?.principalPrivatePerson) && (
                <Button
                  data-testid="view-matter-request-button"
                  type="text"
                  icon={<ArrowRight />}
                  iconPosition="end"
                  className="text-cta"
                  onClick={handleGoToMatterRequest}
                >
                  {getViewLabel(t, 'matterRequest:matterRequest')}
                </Button>
              )
            }
          />
        </Flex>
      </PrimaryCard>
      {/* Stays mounted through its closing animation, which is when antd hands focus back. */}
      <DescriptionModal
        title={showMoreContent?.title ?? ''}
        description={showMoreContent?.content ?? ''}
        open={isShowMoreModalOpen}
        onClose={handleClose}
      />
    </DetailScrollArea>
  );
};

export default ConflictCheckDetail;
